import { useState, useEffect, useCallback } from 'react';
import type { ImageDto } from '@/components/shared/types';
import { getLeadImages, deleteLeadImage } from '../api/leadImages';

interface UseLeadImagesResult {
  images: ImageDto[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  deleteImage: (imageId: number) => Promise<void>;
}

/**
 * Hook to manage lead images
 * - Fetches images for a specific lead
 * - Provides delete functionality
 * - Handles loading and error states
 */
export function useLeadImages(leadId: number): UseLeadImagesResult {
  const [images, setImages] = useState<ImageDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchImages = useCallback(async () => {
    if (!leadId) {
      setImages([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);

    try {
      const data = await getLeadImages(leadId);
      setImages(data);
    } catch (err) {
      setError('Failed to load images');
      console.error('Error fetching lead images:', err);
    } finally {
      setIsLoading(false);
    }
  }, [leadId]);

  const deleteImage = async (imageId: number) => {
    try {
      await deleteLeadImage(leadId, imageId);
      // Remove from local state
      setImages((prev) => prev.filter((img) => img.id !== imageId));
    } catch (err) {
      console.error('Error deleting image:', err);
      throw err;
    }
  };

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  return {
    images,
    isLoading,
    error,
    refetch: fetchImages,
    deleteImage,
  };
}
